import type { EventParticipantRole } from 'models';
import type { ReactNode } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeLineIcon, Presentation1LineIcon, ShieldShapeLineIcon } from 'ui';

export type EventParticipantRoleOption = {
  icon: ReactNode;
  label: string;
  tooltip: string;
  value: EventParticipantRole;
};

export const useGetEventParticipantRoleOption = () => {
  const { t } = useTranslation();

  return useCallback(
    (role: EventParticipantRole): EventParticipantRoleOption => {
      switch (role) {
        case 'attendee':
          return {
            icon: <EyeLineIcon />,
            label: t('Attendee'),
            tooltip: t(
              'Can view webinar and interact via chat, Q&A, polls, and handouts. Audio/video sharing only if enabled by admin.',
            ),
            value: 'attendee',
          };
        case 'moderator':
          return {
            icon: <ShieldShapeLineIcon />,
            label: t('Admin / Host'),
            tooltip: t(
              'Full control over webinar. Can manage all settings, record, end webinar, control attendee permissions, and share content.',
            ),
            value: 'moderator',
          };
        case 'presenter':
          return {
            icon: <Presentation1LineIcon />,
            label: t('Presenter'),
            tooltip: t(
              'Can share content (mic, webcam, files, screen) but cannot access host controls or manage other participants.',
            ),
            value: 'presenter',
          };
      }
    },
    [t],
  );
};
